import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "./DataTable.css";
import { ModuleRegistry, themeQuartz } from "ag-grid-community";
import { AllEnterpriseModule, IntegratedChartsModule } from "ag-grid-enterprise";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";


ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
]);

const DataTable = (
  {
    height,
    columns,
    paginationPageSize,
    paginationPageSizeSelector,
    rowSelection,
    suppressRowClickSelection,
    onRowSelected,
    gridOptions,
    onSelectionChanged,
    headerCheckboxSelectionFilterOnly,
    isCellCheckboxSelection,
    onFirstDataRendered,
    selectionColumnDef,
    getRowStyle,
    getRowId,
    cellEditorParams,
    stopEditingWhenCellsLoseFocus,
    onCellValueChanged,
    onFilterChanged,
    onColumnFilterChanged,
    postFilterChanged,
    suppressMenuHide,
    getRowHeight,
    onPageChange,
    onPageSizeChange,
    rowCount,
    fetchRows,
    defaultPageSize = 10,
    filters,
    showAll = false,
    rows,
    disableSideBar = false,
    initialFilterModel,
    loading = false
  }
) => {
  const gridRef = useRef();
  const [isGridReady, setGridReady] = useState(false)
  const [currentPageSize, setCurrentPageSize] = useState(
    paginationPageSize || defaultPageSize
  );
  const isRestoringFilters = useRef(false);
  const lastForwardedFilterModelRef = useRef(null);
  const fetchRowsRef = useRef(fetchRows);
  const lastDatasourceResetKeyRef = useRef("");

  useEffect(() => {
    fetchRowsRef.current = fetchRows;
  }, [fetchRows]);

  // Handle grid ready and apply initial filter model
  const handleGridReady = useCallback((params) => {
    setGridReady(true);
    if (initialFilterModel && params.api) {
      isRestoringFilters.current = true;
      params.api.setFilterModel(initialFilterModel);
      lastForwardedFilterModelRef.current = JSON.stringify(initialFilterModel || {});
      // Allow filter events after restoration completes
      setTimeout(() => {
        isRestoringFilters.current = false;
      }, 50);
    }
  }, [initialFilterModel]);
  const gridProps = {
    sideBar: {
      toolPanels: disableSideBar ? [] : [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
      ],
      defaultToolPanel: ""
    }
  };

  // ✅ This useEffect now exclusively handles datasource updates.
  useEffect(() => {
    // Wait until the grid API is available.
    if (fetchRows && gridRef.current?.api) {
      const datasourceResetKey = JSON.stringify({
        filters,
        currentPageSize,
        showAll,
      });

      if (datasourceResetKey === lastDatasourceResetKeyRef.current) {
        return;
      }
      lastDatasourceResetKeyRef.current = datasourceResetKey;

      // ... (rest of the function for creating createServerSideDatasource)
      const createServerSideDatasource = () => {
        // ... (implementation of createServerSideDatasource remains the same)
        return {
          getRows: async (gridParams) => {
            try {
              const { request } = gridParams;
              const startRow = request.startRow || 0;
              // ... (rest of getRows implementation remains the same)
              const size = showAll
                ? 1
                : gridRef.current?.api?.paginationGetPageSize() || currentPageSize;
              const page = Math.floor(startRow / size);

              const sortQuery = request.sortModel.map(
                (s) => `${s.colId},${s.sort}`
              );

              const { rows: fetchedRows, totalElements } = await fetchRowsRef.current({
                page,
                size,
                filters: { ...filters },
                sort: sortQuery,
                rowGroupCols: request.rowGroupCols,
                groupKeys: request.groupKeys,
              });

              // Padding to prevent infinite loads if last page is not full
              let paddedRows = fetchedRows;
              if (fetchedRows.length < size && startRow + fetchedRows.length < totalElements) {
                const diff = size - fetchedRows.length;
                const placeholders = Array.from({ length: diff }, (_, i) => ({
                  __placeholder: true,
                  id: `pad-${startRow + fetchedRows.length + i}`,
                }));
                paddedRows = [...fetchedRows, ...placeholders];
              }

              gridParams.success({
                rowData: paddedRows,
                rowCount: totalElements,
              });
            } catch (err) {
              gridParams.fail();
            }
          },
        };
      };

      const datasource = createServerSideDatasource();
      // This is the correct way to apply a new datasource.
      // It will automatically trigger a data fetch.
      gridRef.current.api.setGridOption("serverSideDatasource", datasource);
    }
  }, [filters, fetchRows, currentPageSize, showAll]); // `isGridReady` removed

  const handlePaginationChanged = (params) => {
    if (params.api) {
      const size = params.api.paginationGetPageSize();
      if (size && size !== currentPageSize) {
        setCurrentPageSize(size);
        if (typeof onPageSizeChange === "function") {
          onPageSizeChange(size);
        }
      }
      if (typeof onPageChange === "function") {
        const page = params.api.paginationGetCurrentPage();
        onPageChange(page);
      }
    }
  };

  // Handle AG Grid filter changes for column-level filtering
  const handleFilterChanged = useCallback((params) => {
    // Skip filter change event only during filter restoration
    if (isRestoringFilters.current) {
      return;
    }

    if (onColumnFilterChanged && params.api) {
      const filterModel = params.api.getFilterModel();
      const filterModelSignature = JSON.stringify(filterModel || {});

      if (filterModelSignature === lastForwardedFilterModelRef.current) {
        if (onFilterChanged) {
          onFilterChanged(params);
        }
        return;
      }

      lastForwardedFilterModelRef.current = filterModelSignature;

      // Pass the raw AG Grid filter model (for persistence)
      // and convert to backend format
      const columnFilters = {};
      Object.keys(filterModel).forEach(column => {
        const filter = filterModel[column];

        if (filter.filterType === 'set' && filter.values) {
          columnFilters[column] = filter.values;
        }

        if (filter.filterType === 'text' && filter.filter) {
          columnFilters[column] = filter.filter;
        }

        if (filter.filterType === 'date') {
          columnFilters[column] = {
            type: filter.type, // 'equals', 'greaterThan', 'lessThan', 'inRange', etc.
            dateFrom: filter.dateFrom,
            dateTo: filter.dateTo
          };
        }
      });

      onColumnFilterChanged(columnFilters, filterModel);
    }

    if (onFilterChanged) {
      onFilterChanged(params);
    }
  }, [onColumnFilterChanged, onFilterChanged]);

  return (
    <div
      className="ag-theme-quartz"
      style={{
          width: "100%",
          flex: 1,         // Tells the theme div to fill the Wrapper Box
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }}
    >
      <AgGridReact
        // debug={ true }
        ref={gridRef}
        containerStyle={{ height: "100%", width: "100%" }}
        onGridReady={handleGridReady}
        theme={themeQuartz}
        columnDefs={columns}
        rowSelection={rowSelection || "multiple"}
        suppressRowClickSelection={suppressRowClickSelection}
        enableCharts={true}
        pagination={!showAll}
        paginationPageSize={currentPageSize}
        paginationPageSizeSelector={
          paginationPageSizeSelector || [10, 25, 50]
        }
        rowModelType={fetchRows ? "serverSide" : "clientSide"}
        cacheBlockSize={currentPageSize}
        rowData={fetchRows ? undefined : rows || []}
        rowCount={fetchRows ? undefined : rowCount}
        loading={loading}
        onPaginationChanged={handlePaginationChanged}
        gridOptions={gridOptions}
        onRowSelected={onRowSelected}
        onSelectionChanged={onSelectionChanged}
        headerCheckboxSelectionFilterOnly={
          headerCheckboxSelectionFilterOnly
        }
        isCellCheckboxSelection={isCellCheckboxSelection}
        onFirstDataRendered={onFirstDataRendered}
        selectionColumnDef={selectionColumnDef}
        getRowStyle={getRowStyle}
        getRowId={getRowId}
        cellEditorParams={cellEditorParams}
        stopEditingWhenCellsLoseFocus={
          stopEditingWhenCellsLoseFocus || true
        }
        onCellValueChanged={onCellValueChanged}
        onFilterChanged={handleFilterChanged}
        postFilterChanged={postFilterChanged}
        suppressMenuHide={suppressMenuHide}
        enableCellTextSelection={true}
        getRowHeight={getRowHeight}
        suppressRowTransform={true}
        defaultColDef={{
          sortable: true,
          filter: fetchRows ? false : true,
          resizable: true,
          flex: 1,
          minWidth: 200,
          cellStyle: { textAlign: "left" },
          headerClass: "ag-left-aligned-header",
        }}
        detailRowHeight={100}
        detailCellRendererParams={gridOptions?.detailCellRendererParams}
        {...gridProps}
      />
    </div>
  );
}
  ;

export default DataTable;